import React, { useState } from 'react';
import { useHistory } from 'react-router';

import * as paths from 'app-router/paths';
import { useStore } from 'stores';
import { RequesterToken } from 'stores/RequesterTokenStore';
import { Requester } from 'stores/RequesterStore';
import { SuggestionsSelector } from 'components/Form/SuggestionsSelector';
import { WithAddon } from 'components/Form/WithAddon';
import { ActionIcon, CancelIcon, DownArrowIcon } from 'components/Icon';
import { Message } from 'components/Message';
import { DefaultCopyToClipboard } from 'components/CopyToClipboard';
import { SimpleInlineHelp } from 'components/InlineHelp';

export interface UserInput
{
  requester?: RequesterSelection,
}

class RequesterSelection
{
  requester?: Requester;
}

interface Props
{
  userInput: UserInput,
  saveUserInput: (input: UserInput) => void,
  requesterToken: RequesterToken,
}

export const EditForm: React.FC<Props> = ({userInput, saveUserInput, requesterToken}: Props) =>
{
  const history = useHistory();
  const { uiStore, requesterStore, requesterTokenStore } = useStore();

  const [selectedRequester] = useState<RequesterSelection>(userInput.requester || {requester: requesterToken.requester} || {});
  const [requesterInput, setRequesterInput] = useState(selectedRequester.requester?.name || '');
  const [requesterSuggestions, setRequesterSuggestions] = useState<Requester[]>([]);
  const [requesterSuggestionsLoading, setRequesterSuggestionsLoading] = useState(false);
  const [requesterSuggestionsVisible, setRequesterSuggestionsVisible] = useState(false);

  const requesterInputChangeHandler = async (newValue: string) =>
  {
    setRequesterInput(prev => newValue);
    setRequesterSuggestionsLoading(prev => true);
    const suggestions = await requesterStore.requestSuggestionsAsync(newValue);
    setRequesterSuggestionsLoading(prev => false);
    setRequesterSuggestions(prev => suggestions);
  }
  const requesterInputBlurHandler = async () =>
  {
    setRequesterSuggestionsVisible(prev => false);
    await requesterInputChangeHandler(selectedRequester.requester?.name || '');
  }
  const selectRequesterHandler = async (x?: Requester) =>
  {
    selectedRequester.requester = x;
    await requesterInputChangeHandler(x?.name || '');
  }

  const saveRequesterTokenHandler = async () =>
  {
    saveUserInput({requester: selectedRequester});
    const requesterTokenToken = requesterTokenStore.requesterToken.data?.token || '';
    await requesterTokenStore.updateAsync(requesterTokenToken, selectedRequester.requester?.id || 0);
    if (!requesterTokenStore.requesterToken.isError)
    {
      history.push(paths.buildRequesterTokenViewPath(requesterTokenToken));
    }
  };
  const cancelHandler = () =>
  {
    requesterTokenStore.requesterToken.clearError();
    history.goBack();
  }

  return (
    <>
      <div className="fields">
        <label>
          {uiStore.labels.common.forms.requesterToken.token.label}:<SimpleInlineHelp>{uiStore.labels.help.audience.requester_tokens.token.about}</SimpleInlineHelp>
          <DefaultCopyToClipboard text={requesterToken.token} />
          <input type="text" value={requesterToken.token} readOnly={true} />
        </label>
        <label>
          {uiStore.labels.common.forms.requesterToken.requester.label}:
          <SuggestionsSelector isLoading={requesterSuggestionsLoading}
                            suggestionsVisible={requesterSuggestionsVisible}
                            noSuggestionsView={<Message>{uiStore.labels.common.forms.requesterToken.requester.suggestionsInstruction}</Message>}
                            suggestions={requesterSuggestions}
                            maxSuggestions={10}
                            suggestionKeySelector={x => `${x.id}`}
                            suggestionViewTemplate={x => x.name}
                            onSuggestionSelect={selectRequesterHandler} >
            <WithAddon rightAddon={selectedRequester.requester ? <ActionIcon action={async () => await selectRequesterHandler()}><CancelIcon /></ActionIcon> : <DownArrowIcon />}>
              <input type="text"
                     placeholder={uiStore.labels.common.forms.requesterToken.requester.placeholder}
                     value={requesterInput}
                     onChange={async e => await requesterInputChangeHandler(e.target.value)}
                     onFocus={() => setRequesterSuggestionsVisible(prev => true)}
                     onBlur={() => setTimeout(requesterInputBlurHandler, 500)} />
            </WithAddon>
          </SuggestionsSelector>
        </label>

        <div className="info">
          <p>{uiStore.labels.common.forms.requesterToken.createdBy.apply({by: requesterToken.created_by, at: new Date(Date.parse(requesterToken.created_at || '')).toLocaleString(),})}</p>
          <p>{uiStore.labels.common.forms.requesterToken.updatedBy.apply({by: requesterToken.updated_by, at: new Date(Date.parse(requesterToken.updated_at || '')).toLocaleString(),})}</p>
        </div>
      </div>

      <div className="actions">
        <button onClick={saveRequesterTokenHandler}>{uiStore.labels.common.forms.save}</button>
        <button onClick={cancelHandler}>{uiStore.labels.common.forms.cancel}</button>
      </div>
    </>
  );
};